import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Store, Loader2, Coins, Check, Database, Lock, X, ShieldCheck } from 'lucide-react';
import {
  DataProduct, ScopedExport, listMarketProducts, listMyPurchasedProductIds, purchaseDataProduct,
  getDataProductAccess, getScopedExport, downloadScopedFile,
} from '../services/iotService';

const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff', fontSize: 12 } as const;
const sevColor: Record<string, string> = { LOW: 'bg-emerald-500/15 text-emerald-300', MEDIUM: 'bg-amber-500/15 text-amber-300', HIGH: 'bg-red-500/15 text-red-300' };

const DataMarketView: React.FC = () => {
  const [products, setProducts] = useState<DataProduct[]>([]);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [view, setView] = useState<{ product: DataProduct; data: any } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { const [p, o] = await Promise.all([listMarketProducts(), listMyPurchasedProductIds()]); setProducts(p); setOwned(o); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buy = async (p: DataProduct) => {
    setBusy(p.id); setErr(null);
    try {
      const res = await purchaseDataProduct(p.id);
      if (res?.ok === false) {
        setErr(res.reason === 'INSUFFICIENT_CREDITS' ? `Not enough credits (need ${res.needed}, have ${res.available}).` : 'Purchase failed.');
        return;
      }
      setOwned((s) => new Set(s).add(p.id));
    } catch (e: any) { setErr(e.message || 'Purchase failed.'); }
    finally { setBusy(null); }
  };

  const open = async (p: DataProduct) => {
    setBusy(p.id);
    try { setView({ product: p, data: await getDataProductAccess(p.id) }); }
    catch (e: any) { setErr(e.message || 'Could not open.'); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="border-b border-slate-700 pb-4">
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><Store className="text-indigo-400" size={22} /> Data Market</h1>
        <p className="text-slate-400 text-sm mt-1">Buy IoT datasets from device owners. Paid in credits; sales go to the data owner.</p>
      </div>

      {err && <div className="bg-red-900/20 border border-red-500/30 p-3 rounded-lg text-red-200 text-sm">{err}</div>}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" /></div> : products.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-dashed border-slate-700">
          <Database size={40} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No datasets listed yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => {
            const mine = owned.has(p.id);
            return (
              <div key={p.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-white font-bold">{p.title}</h3>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${sevColor[p.sensitivity] || sevColor.LOW}`}>{p.sensitivity}</span>
                </div>
                {p.description && <p className="text-slate-400 text-sm mt-1 line-clamp-3">{p.description}</p>}
                <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-2">
                  {p.metric && <span className="bg-slate-700 px-1.5 py-0.5 rounded">{p.metric}</span>}
                  {p.sensitivity === 'HIGH' && <span className="text-amber-300">aggregated only</span>}
                </div>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className="text-white font-bold flex items-center gap-1">{p.access === 'FREE' ? 'Free' : <><Coins size={14} className="text-yellow-400" /> {p.price_credits}</>}</span>
                  {mine ? (
                    <button onClick={() => open(p)} disabled={busy === p.id} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5">{busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} View data</button>
                  ) : (
                    <button onClick={() => p.access === 'FREE' ? buy(p).then(() => open(p)) : buy(p)} disabled={busy === p.id} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5">{busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} {p.access === 'FREE' ? 'Get' : 'Buy'}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view && <DataViewer product={view.product} data={view.data} onClose={() => setView(null)} />}
    </div>
  );
};

const RawFiles: React.FC<{ productId: string }> = ({ productId }) => {
  const [exp, setExp] = useState<ScopedExport | null>(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => { (async () => { try { setExp(await getScopedExport(productId)); } catch { /* ignore */ } finally { setLoading(false); } })(); }, [productId]);

  const downloadAll = async () => {
    if (!exp) return;
    const files = exp.files.slice(0, 500);
    setDl({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) { try { await downloadScopedFile(files[i]); } catch { /* skip */ } setDl({ done: i + 1, total: files.length }); }
    setDl(null);
  };

  if (loading) return <p className="text-xs text-slate-500 mt-3">Checking raw files…</p>;
  if (!exp || !exp.configured) return <p className="text-xs text-slate-500 mt-3">Aggregated rollups shown. Raw per-reading export needs the ingestion Worker (<code>VITE_IOT_WORKER_URL</code>).</p>;
  if (!exp.raw_allowed) return <p className="text-xs text-amber-300/80 mt-3">This dataset is high-sensitivity — sold as aggregated rollups only.</p>;
  if (exp.file_count === 0) return <p className="text-xs text-slate-500 mt-3">No raw files in the purchased range yet.</p>;

  return (
    <div className="mt-4 flex items-center gap-3">
      <button onClick={downloadAll} disabled={!!dl} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
        {dl ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
        {dl ? `Downloading ${dl.done}/${dl.total}…` : `Download raw (${exp.file_count}${exp.file_count > 500 ? ', first 500' : ''} files)`}
      </button>
    </div>
  );
};

const DataViewer: React.FC<{ product: DataProduct; data: any; onClose: () => void }> = ({ product, data, onClose }) => {
  const rollups: any[] = data?.rollups || [];
  const byDay: Record<string, any> = {}; const metrics = new Set<string>();
  rollups.forEach((r) => { metrics.add(r.metric); byDay[r.day] = byDay[r.day] || { day: r.day }; byDay[r.day][r.metric] = r.avg; });
  const chart = Object.values(byDay).sort((a: any, b: any) => a.day.localeCompare(b.day));
  const colors = ['#6366F1', '#10B981', '#F59E0B', '#EF4444'];
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `dataset_${product.id}.json`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-400" /> {product.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        {chart.length === 0 ? <p className="text-slate-500 text-center py-8">No aggregated data in range yet.</p> : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} />
              {[...metrics].map((m, i) => <Line key={m} type="monotone" dataKey={m} stroke={colors[i % colors.length]} strokeWidth={2} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        )}
        <div className="flex justify-end mt-4">
          <button onClick={downloadJson} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"><Database size={14} /> Download rollups (JSON)</button>
        </div>
        <RawFiles productId={product.id} />
      </div>
    </div>
  );
};

export default DataMarketView;
